import React from 'react';
import styles from './MentionText.module.css';

interface MentionTextProps {
  text: string;
  className?: string;
}

// Regex to match @[uuid|Display Name] and @[everyone] (case-insensitive UUID)
const MENTION_PATTERN = /@\[([a-fA-F0-9-]+)\|([^\]]+)\]|@\[everyone\]/gi;

// Convert plain text with \n into React nodes with <br /> elements
let keyCounter = 0;
function textWithBreaks(str: string): React.ReactNode[] {
  const lines = str.split('\n');
  const nodes: React.ReactNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) nodes.push(<br key={`br-${keyCounter++}`} />);
    if (line) nodes.push(line);
  });
  return nodes;
}

const MentionText: React.FC<MentionTextProps> = ({ text, className }) => {
  if (!text) return null;
  keyCounter = 0;

  // First names shared by two or more different mentioned users in this text
  // are ambiguous — those mentions show the full name instead.
  const firstNameOwners = new Map<string, Set<string>>();
  const scanRegex = new RegExp(MENTION_PATTERN.source, 'gi');
  let scan;
  while ((scan = scanRegex.exec(text)) !== null) {
    if (scan[1] && scan[2]) {
      const fn = scan[2].trim().split(/\s+/)[0].toLowerCase();
      if (!firstNameOwners.has(fn)) firstNameOwners.set(fn, new Set());
      firstNameOwners.get(fn)!.add(scan[1].toLowerCase());
    }
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(MENTION_PATTERN.source, 'g');

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match (with newline support)
    if (match.index > lastIndex) {
      parts.push(...textWithBreaks(text.slice(lastIndex, match.index)));
    }

    if (match[0] === '@[everyone]') {
      parts.push(
        <span key={`m-${match.index}`} className={styles.mentionEveryone}>@Team</span>
      );
    } else {
      const firstName = match[2].trim().split(/\s+/)[0];
      const ambiguous = (firstNameOwners.get(firstName.toLowerCase())?.size || 0) > 1;
      parts.push(
        <span key={`m-${match.index}`} className={styles.mention}>@{ambiguous ? match[2].trim() : firstName}</span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text (with newline support)
  if (lastIndex < text.length) {
    parts.push(...textWithBreaks(text.slice(lastIndex)));
  }

  return <span className={className}>{parts}</span>;
};

export default MentionText;
